import {
  ApiErrorBody,
  HealthResponse,
  PlayerHistoryResponse,
  PlayerResponse,
  PlayerSearchResponse,
  type PlayerHistoryResponse as PlayerHistory,
  type PlayerResponse as Player,
} from '@omni-torn/shared';
import { z, type ZodTypeAny } from 'zod';
import { renewNow, session } from './app/session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function getJson<S extends ZodTypeAny>(path: string, schema: S, retry = true): Promise<z.infer<S>> {
  let res: Response;
  try {
    const token = session.get()?.token;
    res = await fetch(path, { headers: { accept: 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) } });
  } catch {
    throw new ApiError(0, 'network', 'Could not reach the Omni-Torn server.');
  }
  const body: unknown = await res.json().catch(() => null);
  // The sign-in ran out: renew it once and ask again.
  if (res.status === 401 && retry && session.get() && (await renewNow())) return getJson(path, schema, false);
  if (!res.ok) {
    const err = ApiErrorBody.safeParse(body);
    if (err.success) throw new ApiError(res.status, err.data.error.code, err.data.error.message);
    throw new ApiError(res.status, 'unknown', `Server returned HTTP ${res.status}.`);
  }
  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new ApiError(res.status, 'contract', 'The server sent data in an unexpected format.');
  return parsed.data;
}


export const api = {
  player: (id: number, refresh = false): Promise<Player> =>
    getJson(`/api/players/${id}${refresh ? '?refresh=1' : ''}`, PlayerResponse),
  history: (id: number): Promise<PlayerHistory> => getJson(`/api/players/${id}/history`, PlayerHistoryResponse),
  health: () => getJson('/api/health', HealthResponse),
  search: (q: string, remote: boolean) =>
    getJson(`/api/players/search?q=${encodeURIComponent(q)}${remote ? '&remote=1' : ''}`, PlayerSearchResponse),
};
